import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { LessonType, VideoProvider } from '../apis/api-client';
import { storageClient } from '../apis/client-instances';
import { FileDropzone, SectionHeader, TiptapEditor } from '../components/common';
import VideoSourceEditor, { VideoSourceEditorHandle } from '../components/common/VideoSourceEditor';
import { alert, getFileUrl, uploadFile } from '../hooks/function';
import RichHtmlSourceEditor, { isRichHtml } from './lessonEditor/RichHtmlSourceEditor';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  SelectField,
  TextField,
  Typography,
} from './ui';
const LESSON_TYPES = Object.values(LessonType)
  .filter((v) => typeof v === 'string')
  .map((v) => ({
    label: v === 'Text' ? 'Text/Html' : v,
    key: LessonType[v],
  }));

interface EditLessonDialogProps {
  open: boolean;
  onSave: (lesson: any) => Promise<any>;
  onClose: () => void;
  lessonToEdit?: any;
  modules?: any[];
}

export default function EditLessonDialog({ open, onSave, onClose, lessonToEdit, modules = [] }: EditLessonDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [lessonType, setLessonType] = useState(1);
  const [moduleId, setModuleId] = useState<number | null>(null);
  const [attachedUrl, setAttachedUrl] = useState('');
  const [file, setFile] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [passThreshold, setPassThreshold] = useState(80);
  const [errors, setErrors] = useState({} as any);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoProvider, setVideoProvider] = useState<VideoProvider>(VideoProvider.Mux);
  const [externalVideoId, setExternalVideoId] = useState('');
  const [scormZip, setScormZip] = useState<File | null>(null);
  const [scormUploading, setScormUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Tracks the lesson id across save → upload-kickoff so the shared video
  // component can request a Mux upload URL using the freshly-created lesson's
  // id (state updates wouldn't be visible to the closure synchronously).
  const lessonIdRef = useRef<number | null>(null);
  const videoEditorRef = useRef<VideoSourceEditorHandle | null>(null);

  // Prefill fields when editing
  useEffect(() => {
    if (lessonToEdit) {
      setTitle(lessonToEdit.title || '');
      setDescription(lessonToEdit.description || '');
      setLessonType(lessonToEdit.lessonType || 1);
      setAttachedUrl(lessonToEdit.attachedUrl || '');
      setTextContent(lessonToEdit.textContent || '');
      setImagePreview(lessonToEdit.thumbnail || '');
      setModuleId(lessonToEdit.moduleId || null);
      setPassThreshold(lessonToEdit.quizSettings?.passThreshold ?? 80);
      setVideoProvider(lessonToEdit.videoProvider ?? VideoProvider.Mux);
      setExternalVideoId(lessonToEdit.externalVideoId || '');
      lessonIdRef.current = lessonToEdit.id ?? null;
    } else {
      lessonIdRef.current = null;
    }
  }, [lessonToEdit, open]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setThumbnail(null);
    setImagePreview(null);
    setSelectedFile(null);
    setLessonType(1);
    setModuleId(null);
    setPassThreshold(80);
    setAttachedUrl('');
    setFile(null);
    setTextContent('');
    setErrors({});
    setVideoProvider(VideoProvider.Mux);
    setExternalVideoId('');
    setScormZip(null);
    setScormUploading(false);
    lessonIdRef.current = null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setSelectedThumbnail(file);
  };

  const validate = () => {
    const newErrors: any = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';

    // Quiz type only needs title/description - questions are added after save
    if (lessonType !== LessonType.Quiz) {
      // Video validation lives in the shared VideoSourceEditor; route its
      // error to the appropriate field key so existing UI helpers light up.
      if (lessonType === LessonType.Video) {
        const videoErr = videoEditorRef.current?.validate() ?? null;
        if (videoErr) {
          newErrors[videoProvider === VideoProvider.Wistia ? 'externalVideoId' : 'video'] = videoErr;
        }
      }

      // For PPT/Word/PDF: only require file if there's no existing file AND no new file selected
      if ((lessonType === LessonType.PPT || lessonType === LessonType.Word || lessonType === LessonType.PDF) && !selectedFile && !lessonToEdit?.file) {
        newErrors.selectedFile = 'File is required';
      }

      if (lessonType === LessonType.Text && !textContent.trim()) {
        newErrors.textContent = 'Content is required';
      }

      // SCORM: require a zip on create. On edit, keep the existing package if
      // no new zip is selected (allowing metadata-only edits).
      if (lessonType === LessonType.Scorm && !lessonToEdit && !scormZip) {
        newErrors.scormZip = 'SCORM package (.zip) is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    let uploadedThumbnail;
    let uploadedAttachment;

    setLoading(true);
    // const thumbNailResult = await uploadFile(selectedThumbnail);

    // if (!thumbNailResult.success) {
    //     console.error('Upload failed:', thumbNailResult.error);
    //     alert(enqueueSnackbar, "Failed to save", 'error');
    //     setLoading(false);
    //     return;
    // }

    // uploadedThumbnail = selectedThumbnail ? thumbNailResult.uploadedUrl : lessonToEdit?.thumbnail;

    if (lessonType === LessonType.PPT || lessonType === LessonType.Word || lessonType === LessonType.PDF) {
      if (selectedFile) {
        const result = await uploadFile(selectedFile);
        uploadedAttachment = result?.uploadedUrl;
      } else if (lessonToEdit?.file) {
        uploadedAttachment = lessonToEdit?.file;
      } else {
        return;
      }

      if (!uploadedAttachment) {
        alert(enqueueSnackbar, 'File upload failed', 'error');
        setLoading(false);
        return;
      }

      // if (!uploadedFileUrl) {
      //     console.error('Upload failed:', result.error);
      //     alert(enqueueSnackbar, "Failed to save", 'error');
      //     setLoading(false);
      //     return;
      // };
      setFile(uploadedAttachment);
    }

    const lessonItem = {
      title,
      description,
      // thumbnail: uploadedThumbnail,
      lessonType,
      moduleId,
      attachedUrl: lessonType === LessonType.Video ? attachedUrl : '',
      file: lessonType === LessonType.PPT || lessonType === LessonType.Word || lessonType === LessonType.PDF ? uploadedAttachment : null,
      fileUrl: lessonType === LessonType.PPT || lessonType === LessonType.Word || lessonType === LessonType.PDF ? await getFileUrl(uploadedAttachment) : null,
      textContent: lessonType === LessonType.Text ? textContent : '',
      quizSettings: lessonType === LessonType.Quiz ? { passThreshold } : null,
      videoProvider: lessonType === LessonType.Video ? videoProvider : null,
      externalVideoId: lessonType === LessonType.Video && videoProvider === VideoProvider.Wistia ? externalVideoId.trim() : null,
    };

    // Quiz lessons: save and close (questions are added via QuizEditor after lesson exists)
    if (lessonType === LessonType.Quiz) {
      await onSave(lessonItem);
      reset();
      onClose();
      return;
    }

    // SCORM lessons: pack the zip onto lessonItem and let useLessonManagement
    // handle the two-step save (create row → upload zip → patch local state).
    // Doing it in the hook keeps lesson state management consolidated and
    // ensures the newly-created lesson is selected with the SCORM fields
    // populated, so ScormViewer can immediately play it.
    if (lessonType === LessonType.Scorm) {
      if (scormZip) {
        (lessonItem as any).scormZip = scormZip;
        setScormUploading(true);
      }
      try {
        await onSave(lessonItem);
      } finally {
        setScormUploading(false);
      }
      reset();
      onClose();
      return;
    }

    // Save lesson metadata first (always — including for Mux video edits) so
    // title/description changes persist regardless of upload outcome.
    let savedLesson;
    try {
      savedLesson = await onSave(lessonItem);
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert(enqueueSnackbar, 'Failed to save lesson', 'error');
      setLoading(false);
      return;
    }

    // Capture the saved id so the video editor's getMuxUploadUrl callback can
    // reach it synchronously for new-lesson uploads.
    if (savedLesson?.id) lessonIdRef.current = savedLesson.id;

    // Mux video lesson with a pending file → kick off the upload now that the
    // lesson row exists. Dialog stays open until the upload completes (or
    // errors, in which case the user can retry).
    if (lessonType === LessonType.Video && videoEditorRef.current?.hasPendingUpload()) {
      const ok = await videoEditorRef.current.startUploadIfPending();
      setLoading(false);
      if (!ok) return;
      alert(enqueueSnackbar, 'Video uploaded successfully! It may take a few minutes for your video to appear.', 'success');
    }

    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>{lessonToEdit ? 'Edit Lesson' : 'Add Lesson'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 3, overflow: 'hidden' }}>
        <Grid container spacing={3} sx={{ flex: 1, height: '100%' }}>
          {/* Left Column - Meta Fields */}
          <Grid item xs={12} md={4}>
            <Box sx={{ pr: { md: 1 } }}>
              {/* Title */}
              <TextField
                label="Title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setErrors((prev) => ({ ...prev, title: '' }));
                }}
                fullWidth
                error={!!errors.title}
                helperText={errors.title}
              />

              {/* Description */}
              <TextField
                label="Description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, description: '' }));
                }}
                fullWidth
                multiline
                rows={4}
                margin="normal"
                error={!!errors.description}
                helperText={errors.description}
              />

              {/* Lesson Type */}
              <SelectField
                label="Lesson Type"
                value={lessonType}
                onChange={(e) => setLessonType(e.target.value)}
                options={LESSON_TYPES.map((type) => ({ label: type.label, value: type.key }))}
                allowEmpty={false}
                fullWidth
                sx={{ mt: 2, mb: 1 }}
              />

              {/* Module/Section Selector */}
              {modules.length > 0 && (
                <SelectField
                  label="Section"
                  value={moduleId || ''}
                  onChange={(e) => setModuleId(e.target.value ? Number(e.target.value) : null)}
                  options={modules.map((m) => ({ label: m.title, value: m.id }))}
                  allowEmpty={true}
                  emptyLabel="(No section)"
                  fullWidth
                  sx={{ mt: 2, mb: 1 }}
                />
              )}

              {/* Pass Threshold - only for Quiz type */}
              {lessonType === LessonType.Quiz && (
                <TextField
                  label="Pass Threshold (%)"
                  type="number"
                  value={passThreshold}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, Number(e.target.value)));
                    setPassThreshold(val);
                  }}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 0, max: 100 }}
                />
              )}
            </Box>
          </Grid>

          {/* Right Column - Content Area */}
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box
              sx={{
                pl: { md: 2 },
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'grey.300' },
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
              }}
            >
              <SectionHeader variant="accent" title="Lesson Content" />

              {/* Video Source — shared editor handles Mux upload + Wistia ID
                  paths. Save handler invokes its imperative startUploadIfPending
                  ref method after the lesson row is persisted. */}
              {lessonType === LessonType.Video && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
                  <VideoSourceEditor
                    ref={videoEditorRef}
                    provider={videoProvider}
                    onProviderChange={(provider) => {
                      setVideoProvider(provider);
                      setErrors((prev) => ({ ...prev, video: '', externalVideoId: '' }));
                    }}
                    externalVideoId={externalVideoId}
                    onExternalVideoIdChange={(value) => {
                      setExternalVideoId(value);
                      setErrors((prev) => ({ ...prev, externalVideoId: '' }));
                    }}
                    externalVideoIdError={errors.externalVideoId}
                    existing={lessonToEdit ? {
                      muxUploadId: lessonToEdit.muxUploadId,
                      muxPlaybackId: lessonToEdit.muxPlaybackId,
                      videoStatus: lessonToEdit.videoStatus,
                      videoDuration: lessonToEdit.videoDuration,
                    } : undefined}
                    requireFileOnCreate={!lessonToEdit}
                    getMuxUploadUrl={async () => {
                      const id = lessonIdRef.current;
                      if (!id) throw new Error('Lesson must be saved before uploading');
                      const r = await storageClient.generateVideoUpload({ lessonId: id });
                      return r.uploadUrl;
                    }}
                    onUploadError={(msg) => alert(enqueueSnackbar, `Upload failed: ${msg}`, 'error')}
                  />
                </Box>
              )}

              {/* PPT/Word/PDF Upload */}
              {(lessonType === LessonType.PPT || lessonType === LessonType.Word || lessonType === LessonType.PDF) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
                  <FileDropzone
                    accept={
                      lessonType === LessonType.PDF
                        ? '.pdf,application/pdf'
                        : lessonType === LessonType.PPT
                        ? '.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation'
                        : '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    }
                    value={selectedFile}
                    onChange={(file) => {
                      setSelectedFile(file);
                      setErrors((prev) => ({ ...prev, selectedFile: '' }));
                    }}
                    error={errors.selectedFile}
                    label={`${LESSON_TYPES.find((l) => l.key === lessonType)?.label} file`}
                    existingFileName={lessonToEdit?.file ? lessonToEdit.file.split('/').pop()?.split('?')[0] : undefined}
                    existingFileUrl={lessonToEdit?.file}
                  />
                </Box>
              )}

              {/* SCORM package upload */}
              {lessonType === LessonType.Scorm && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
                  <FileDropzone
                    accept=".zip,application/zip,application/x-zip-compressed"
                    value={scormZip}
                    onChange={(file) => {
                      setScormZip(file);
                      setErrors((prev) => ({ ...prev, scormZip: '' }));
                    }}
                    error={errors.scormZip}
                    label="SCORM package (.zip)"
                    existingFileName={lessonToEdit?.scormSourceFilename || undefined}
                  />
                  {lessonToEdit?.scormPackageUrl && !scormZip && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      A SCORM package is already uploaded. Drop a new .zip to replace it, or save without
                      changes to keep the existing one.
                    </Typography>
                  )}
                  {scormUploading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Uploading and processing SCORM package…
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Quiz - questions are edited inline in the lesson viewer */}
              {lessonType === LessonType.Quiz && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {lessonToEdit?.id
                      ? 'Quiz questions can be edited from the lesson screen.'
                      : 'Save the lesson first, then you can add quiz questions from the lesson screen.'}
                  </Typography>
                </Box>
              )}

              {/* Rich Text Editor — Tiptap for plain content, raw HTML source for imported/rich content */}
              {lessonType === LessonType.Text && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
                  {isRichHtml(textContent) ? (
                    <RichHtmlSourceEditor
                      value={textContent}
                      onChange={setTextContent}
                      sx={{ height: '100%' }}
                    />
                  ) : (
                    <TiptapEditor
                      content={textContent}
                      onChange={setTextContent}
                      placeholder="Write your text lesson content here..."
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    />
                  )}
                  {errors.textContent && <FormHelperText error sx={{ mt: 1 }}>{errors.textContent}</FormHelperText>}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Saving...' : lessonToEdit ? 'Save Changes' : 'Add Lesson'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
